"use client";

import { lazy, ReactNode, Suspense, useMemo } from "react";
import { usePathname } from "next/navigation";
import { CustomScrollbar } from "@/shared/components/CustomScrollbar";
import { FirebaseLocalhostRedirect } from "@/shared/components/FirebaseLocalhostRedirect";
import { PopupScrollLock } from "@/shared/components/PopupScrollLock";
import { AppAuthSnapshotProvider } from "@/shared/providers/AppAuthSnapshot";
import { useAuthStore } from "@/shared/store/authStore";

const FullAppProviders = lazy(() => import("@/shared/providers/FullAppProviders"));
const FULL_PROVIDER_PREFIXES = [
  "/auth",
  "/user",
  "/provider",
  "/admin",
  "/super-admin",
  "/terms-and-conditions",
  "/privacy-policy",
] as const;

function needsFullProviders(pathname: string) {
  return FULL_PROVIDER_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));
}

export default function AppProviders({ children }: { children: ReactNode }) {
  const pathname = usePathname();
  const hasHydrated = useAuthStore((state) => state.hasHydrated);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const isAuthRouting = useAuthStore((state) => state.isAuthRouting);
  const isLogoutPending = useAuthStore((state) => state.isLogoutPending);
  const role = useAuthStore((state) => state.role);
  const setLogoutPending = useAuthStore((state) => state.setLogoutPending);
  const authSnapshot = useMemo(
    () => ({ hasHydrated, isAuthenticated, isAuthRouting, isLogoutPending, role: role ?? null, setLogoutPending }),
    [hasHydrated, isAuthenticated, isAuthRouting, isLogoutPending, role, setLogoutPending],
  );

  if (isAuthenticated || needsFullProviders(pathname)) {
    return (
      <AppAuthSnapshotProvider value={authSnapshot}>
        <Suspense fallback={null}>
          <FullAppProviders>{children}</FullAppProviders>
        </Suspense>
      </AppAuthSnapshotProvider>
    );
  }

  return (
    <AppAuthSnapshotProvider value={authSnapshot}>
      <FirebaseLocalhostRedirect />
      <PopupScrollLock />
      <CustomScrollbar />
      {children}
    </AppAuthSnapshotProvider>
  );
}
